// JOBE-0044.14: per-chip evaluator for `dropSuggestion`. Mirrors the
// chip vocabulary `.4` ships (type / owner / by / project / in /
// since / until / q). Predicates that can't be evaluated client-side
// (e.g. a hypothetical `roleInDepartment` that requires the org graph)
// return `true` — best-effort, preserves the heuristic's non-zero-count
// guarantee per spec §Drop-suggestion heuristic.

function lc(s) { return typeof s === 'string' ? s.toLowerCase() : ''; }

function valuesIncludeAny(values, candidates) {
  const set = new Set((values || []).map(lc));
  return candidates.some((c) => set.has(lc(c)));
}

// matchesChip(row, chip) — return true if the row would survive the
// chip filter applied client-side.
export function matchesChip(row, chip) {
  if (!chip || chip.kind === 'scope') return true;
  if (chip.kind !== 'predicate') return true;
  const values = chip.values || [];
  if (values.length === 0) return true;

  switch (chip.friendlyToken) {
    case 'type': {
      return valuesIncludeAny(values, [row?.kind]);
    }
    case 'owner': {
      const candidates = [
        row?.owner?.urn,
        row?.owner?.name,
        row?.owner?.displayName,
      ];
      return valuesIncludeAny(values, candidates);
    }
    case 'by': {
      // `committedBy` — v1 rows don't carry a dedicated committer
      // field; fall back to owner identifiers for the heuristic.
      const candidates = [
        row?.committedBy?.urn,
        row?.committedBy?.name,
        row?.committedBy?.displayName,
        row?.owner?.urn,
        row?.owner?.name,
        row?.owner?.displayName,
      ];
      return valuesIncludeAny(values, candidates);
    }
    case 'project': {
      const candidates = [row?.project?.urn, row?.project?.name];
      return valuesIncludeAny(values, candidates);
    }
    case 'in': {
      const collections = Array.isArray(row?.collections) ? row.collections : [];
      const candidates = [];
      for (const c of collections) {
        candidates.push(c?.urn, c?.slug, c?.name);
      }
      return valuesIncludeAny(values, candidates);
    }
    case 'since': {
      const cutoff = Date.parse(values[0]);
      const at = Date.parse(row?.lastChangedAt);
      if (Number.isNaN(cutoff) || Number.isNaN(at)) return true;
      return at >= cutoff;
    }
    case 'until': {
      const cutoff = Date.parse(values[0]);
      const at = Date.parse(row?.lastChangedAt);
      if (Number.isNaN(cutoff) || Number.isNaN(at)) return true;
      return at < cutoff;
    }
    case 'q': {
      const needle = lc(values[0] || '');
      if (!needle) return true;
      const hay = [
        row?.name,
        row?.summary,
        row?.urn,
        row?.kind,
      ].map(lc).join(' ');
      return hay.includes(needle);
    }
    default:
      // Unknown predicate — non-evaluable client-side; best-effort.
      return true;
  }
}

export function matchesAllChips(row, chips) {
  if (!Array.isArray(chips) || chips.length === 0) return true;
  for (const chip of chips) {
    if (!matchesChip(row, chip)) return false;
  }
  return true;
}
